/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import {loadModules} from 'esri-loader';
import getLocationMarker from '../markers/LocationMarkers';

type LocationMarkerProps = {
  view: __esri.MapView;
  map: __esri.Map;
  isVisible: boolean;
  isFMTracking?: boolean;
};

/**
 * Defines the unique identifier for the location marker GraphicsLayer
 * as the layer id.
 */
export const LOCATION_MARKER_LAYER_ID = 'locationMarker';

/**
 * @useLocationMarker
 *
 * Provides a hook for automatically adding and removing a location based marker
 * to a MapView.
 */
export const useLocationMarker = ({
  view,
  map,
  isVisible,
  isFMTracking = false
}: LocationMarkerProps) => {
  const graphicRef        = useRef(null);
  const markerRef         = useRef(null);
  const graphicsLayerRef  = useRef(null);
  const isFMTrackingRef   = useRef(isFMTracking);

  useEffect(() => {
    isFMTrackingRef.current = isFMTracking;
  }, [isFMTracking]);

  const removeMarker = useCallback(() => {
    if (graphicsLayerRef?.current && markerRef?.current) {
      graphicsLayerRef.current.remove(markerRef.current);
    }
  }, [map]);

  useEffect(() => {
    if (!isFMTrackingRef.current) {
      loadModules(['esri/Graphic', 'esri/layers/GraphicsLayer']).then(([Graphic, GraphicsLayer]) => {
        graphicRef.current = Graphic;
        graphicsLayerRef.current = GraphicsLayer;
      });
    } else {
      graphicRef.current = Graphic;
      graphicsLayerRef.current = GraphicsLayer;
    }
    
    return function() {
      markerRef.current  = null;
      graphicRef.current = null;
      graphicsLayerRef.current = null;
      removeMarker();
    };
  }, []);

  /**
   * Adds a location marker to the underlying map representing the current search
   * location.
   */
  const addMarker = useCallback(async(latitude: number, longitude: number, text: string) => {
    const Graphic = graphicRef.current;
    if (Graphic) {
      removeMarker();
      const marker  = new Graphic({
        attributes : {text, id: text},
        geometry   : {
          type : 'point',
          latitude,
          longitude
        },
        symbol : {
          type   : 'picture-marker',
          url    : getLocationMarker(),
          width  : 24,
          height : 24
        }
      });
      const isLayerExisting = map.layers.some(l => l.id === LOCATION_MARKER_LAYER_ID);
      if (!isLayerExisting) {
        const GraphicsLayer = graphicsLayerRef.current;
        graphicsLayerRef.current = new GraphicsLayer({
          id : LOCATION_MARKER_LAYER_ID
        });
        map.add(graphicsLayerRef.current);
      }
      graphicsLayerRef.current?.add?.(marker);
      markerRef.current = marker;
    }
  }, [map]);

  if (!isVisible) {
    removeMarker();
  }
  return [addMarker, removeMarker];
};
